import { API_URL } from '../config';

// Global token expiration handler - can be set by AuthContext
let globalTokenExpiredHandler: (() => void) | null = null;

// Global navigation handler - can be set by App component
let globalNavigateToLogin: (() => void) | null = null;

export const setGlobalTokenExpirationHandler = (handler: () => void) => {
  globalTokenExpiredHandler = handler;
};

export const setGlobalNavigateToLogin = (handler: () => void) => {
  globalNavigateToLogin = handler;
};

// Centralized API fetch function with token expiration handling
export async function fetchApi<T>(
  url: string, 
  options: RequestInit = {}, 
  includeAuth: boolean = true
): Promise<T> {
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Merge existing headers from options
  if (options.headers) {
    Object.entries(options.headers).forEach(([key, value]) => {
      if (typeof value === 'string') {
        requestHeaders[key] = value;
      }
    });
  }

  // Add auth token if requested and available
  if (includeAuth) {
    const token = localStorage.getItem('authToken');
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, { 
    ...options, 
    headers: requestHeaders 
  });

  // Handle token expiration
  if (response.status === 401) {
    const errorData = await response.json().catch(() => ({ message: 'Unauthorized' }));
    
    // Check if it's a token expiration issue
    if (errorData.message?.toLowerCase().includes('expired') || 
        errorData.message?.toLowerCase().includes('invalid') ||
        errorData.message?.toLowerCase().includes('unauthorized')) {
      console.warn('API call failed: Token expired or invalid');
      
      // Clear local storage immediately
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      
      // Call the global logout handler if available
      if (globalTokenExpiredHandler) {
        globalTokenExpiredHandler();
      }
      
      // Navigate to login if handler is available
      if (globalNavigateToLogin) {
        globalNavigateToLogin();
      }
      
      throw new Error('Your session has expired. Please log in again.');
    }
    
    throw new Error(errorData.message || 'Not authorized');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  // Handle empty responses (e.g., from PUT/DELETE requests)
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  } else if (response.status === 204 || response.headers.get('content-length') === '0') {
    return {} as T;
  } else {
    const text = await response.text();
    return (text ? { message: text } : {}) as T;
  }
}

export const isApiUrl = (url: string): boolean => {
  try {
    const target = new URL(url, window.location.origin);
    const apiHost = new URL(API_URL, window.location.origin);
    if (target.origin !== apiHost.origin) return false;
    const apiPath = apiHost.pathname.endsWith('/') ? apiHost.pathname : `${apiHost.pathname}/`;
    const targetPath = target.pathname.endsWith('/') ? target.pathname : `${target.pathname}/`;
    return targetPath.startsWith(apiPath);
  } catch {
    return false;
  }
};

export const authedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('authToken');
  const headers = new Headers(options.headers as HeadersInit | undefined);
  if (token && !headers.has('Authorization') && isApiUrl(url)) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const response = await fetch(url, { ...options, headers });
  if (response.status === 401 && isApiUrl(url)) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    if (globalTokenExpiredHandler) globalTokenExpiredHandler();
    if (globalNavigateToLogin) globalNavigateToLogin();
  }
  return response;
};

// Convenience functions for common HTTP methods
export const apiGet = <T>(url: string, includeAuth: boolean = true): Promise<T> =>
  fetchApi<T>(url, { method: 'GET' }, includeAuth);

export const apiPost = <T>(url: string, data?: unknown, includeAuth: boolean = true): Promise<T> =>
  fetchApi<T>(url, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined
  }, includeAuth);

export const apiPut = <T>(url: string, data?: unknown, includeAuth: boolean = true): Promise<T> =>
  fetchApi<T>(url, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined
  }, includeAuth);

export const apiDelete = <T>(url: string, includeAuth: boolean = true): Promise<T> => 
  fetchApi<T>(url, { method: 'DELETE' }, includeAuth); 